import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { Avatar } from './ui';
import type { Notification } from '../types/phase1';

export const NotificationBell: React.FC = () => {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications
  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('notifications')
          .select(`
            *,
            actor:profiles!actor_id(id, username, full_name, avatar_url)
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (fetchError) {
          // Table might not exist yet
          if (fetchError.code === '42P01' || fetchError.message.includes('does not exist')) {
            setError('setup');
          } else {
            console.error('Notification error:', fetchError);
            setError('error');
          }
          return;
        }

        if (data) {
          setNotifications(data);
          setUnreadCount(data.filter(n => !n.is_read).length);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError('error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();

    // Subscribe to new notifications
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          // Fetch full notification with actor
          const { data: newNotif } = await supabase
            .from('notifications')
            .select(`
              *,
              actor:profiles!actor_id(id, username, full_name, avatar_url)
            `)
            .eq('id', payload.new.id)
            .single();

          if (newNotif) {
            setNotifications(prev => [newNotif, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id: string) => {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);

      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user?.id)
        .eq('is_read', false);

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      const notif = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notif && !notif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const getNotificationLink = (notif: Notification): string => {
    switch (notif.reference_type) {
      case 'post':
        return `/feed?post=${notif.reference_id}`;
      case 'comment':
        return `/feed?comment=${notif.reference_id}`;
      case 'message':
        return `/messages?chat=${notif.reference_id}`;
      case 'event':
        return `/events/${notif.reference_id}`;
      default:
        return '#';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return '@';
      case 'dm':
        return '💬';
      case 'reaction':
        return '❤️';
      case 'follow':
        return '👤';
      case 'event':
        return '📅';
      case 'achievement':
        return '🏆';
      default:
        return '🔔';
    }
  };

  const formatTime = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-green-500 transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-slate-900 border border-slate-800 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h3 className="font-bold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-green-500 hover:text-green-400"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-spin inline-block w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full" />
              </div>
            ) : error ? (
              <div className="p-8 text-center text-slate-400">
                <Bell size={32} className="mx-auto mb-2 opacity-50" />
                <p className="mb-2">
                  {error === 'setup' 
                    ? 'Notifications coming soon!' 
                    : 'Could not load notifications'}
                </p>
                <p className="text-xs text-slate-500">
                  {error === 'setup' 
                    ? 'This feature is being set up' 
                    : 'Try again later'}
                </p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell size={32} className="mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  You'll be notified when someone follows you, mentions you, etc.
                </p>
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 p-4 border-b border-slate-800 hover:bg-slate-800/50 transition-colors ${
                    !notif.is_read ? 'bg-green-500/5' : ''
                  }`}
                >
                  {/* Avatar or Icon */}
                  {notif.actor ? (
                    <Link to={`/profile/${notif.actor.username}`}>
                      <Avatar
                        src={notif.actor.avatar_url}
                        fallback={notif.actor.full_name}
                        size="sm"
                      />
                    </Link>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-lg">
                      {getNotificationIcon(notif.type)}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={getNotificationLink(notif)}
                      onClick={() => {
                        if (!notif.is_read) markAsRead(notif.id);
                        setIsOpen(false);
                      }}
                      className="block"
                    >
                      <p className="text-sm text-white">
                        {notif.actor && (
                          <span className="font-medium">{notif.actor.full_name} </span>
                        )}
                        {notif.title}
                      </p>
                      {notif.body && (
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {notif.body}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </Link>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {!notif.is_read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="p-1 text-slate-400 hover:text-green-500"
                        title="Mark as read"
                      >
                        <Check size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notif.id)}
                      className="p-1 text-slate-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Unread indicator */}
                  {!notif.is_read && (
                    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <Link
              to="/notifications"
              onClick={() => setIsOpen(false)}
              className="block p-3 text-center text-sm text-green-500 hover:text-green-400 border-t border-slate-800"
            >
              View all notifications
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
